import { FC } from 'react';
import { BiStopwatch } from 'react-icons/bi';
import { BsCurrencyPound } from 'react-icons/bs';
import { HiArrowNarrowDown, HiArrowNarrowUp } from 'react-icons/hi';
import { MdPerson } from 'react-icons/md';
import { Icon } from '../../../components/shared/icons/Icon';
import { ExplainerModal } from '../../../components/shared/modal/ExplainerModal';
import { SkeletonLoader } from '../../../components/shared/SkeletonLoader';
import { displayCost, displayHrsMinTime, displayStaff } from '../../../helpers/calculationsSummaryShiftsReportHelper';
import strings from '../home.translations';
import { SummaryData } from './buildSummaryData';

interface ResourcesCostSummaryCardProps {
  type: ResourcesCostType;
  title: string;
  summaryData: SummaryData | undefined;
  fetching: boolean;
  dayDiff: number;
}

export enum ResourcesCostType {
  TIME,
  COST,
  STAFF
}

export const ResourcesCostSummaryCard: FC<ResourcesCostSummaryCardProps> = ({
  type,
  title,
  summaryData,
  fetching,
  dayDiff
}) => {
  const computeIcon = () => {
    switch (type) {
      case ResourcesCostType.TIME:
        return <Icon Icon={BiStopwatch} theme="primary" big />;
      case ResourcesCostType.COST:
        return <Icon Icon={BsCurrencyPound} theme="primary" big />;
      case ResourcesCostType.STAFF:
        return <Icon Icon={MdPerson} theme="primary" big />;
    }
  };

  const formatCurrentVal = () => {
    switch (type) {
      case ResourcesCostType.TIME:
        return displayHrsMinTime(summaryData!.timeInMinutes);
      case ResourcesCostType.COST:
        return displayCost(summaryData!.costInPounds);
      case ResourcesCostType.STAFF:
        return displayStaff(summaryData!.totalStaff);
    }
  };

  const getDifferenceArrowIcon = (dataDifferenceType: number) => {
    return dataDifferenceType > 0 ? (
      <HiArrowNarrowUp className="text-primary-500" />
    ) : dataDifferenceType < 0 ? (
      <HiArrowNarrowDown className="text-primary-500" />
    ) : (
      ''
    );
  };

  const getDifferenceText = (dataDifferenceType: number, type: ResourcesCostType) => {
    let displayText: string = '';
    const moreOrLess = dataDifferenceType > 0 ? strings.common.more : dataDifferenceType < 0 ? strings.common.less : '';

    if (dataDifferenceType === 0) {
      displayText = strings.common.totalHasNotChanged(title);
      return displayText;
    }

    switch (type) {
      case ResourcesCostType.TIME:
        displayText = displayHrsMinTime(summaryData!.timeDifferenceInMinutes) + ' ' + moreOrLess;
        return displayText;
      case ResourcesCostType.COST:
        displayText = displayCost(summaryData!.costDifferenceInPounds) + ' ' + moreOrLess;
        return displayText;
      case ResourcesCostType.STAFF:
        let absStaffDifference = Math.abs(summaryData!.totalStaffDifference);
        displayText = displayStaff(absStaffDifference) + ' ' + moreOrLess;
        return displayText;
    }
    return displayText;
  };

  const formatDifferenceVal = () => {
    const textStyle = 'font-bold text-primary-500';
    switch (type) {
      case ResourcesCostType.TIME:
        return (
          <>
            {getDifferenceArrowIcon(summaryData!.timeDifferenceInMinutes)}{' '}
            <p className={textStyle}>{getDifferenceText(summaryData!.timeDifferenceInMinutes, type)}</p>
          </>
        );
      case ResourcesCostType.COST:
        return (
          <>
            {getDifferenceArrowIcon(summaryData!.costDifferenceInPounds)}{' '}
            <p className={textStyle}>{getDifferenceText(summaryData!.costDifferenceInPounds, type)}</p>
          </>
        );
      case ResourcesCostType.STAFF:
        return (
          <>
            {getDifferenceArrowIcon(summaryData!.totalStaffDifference)}{' '}
            <p className={textStyle}>{getDifferenceText(summaryData!.totalStaffDifference, type)}</p>
          </>
        );
    }
  };

  return (
    <div className="flex flex-col lg:flex-row border-b lg:border-r lg:border-b-0 last:border-0 p-2 w-full h-full">
      <div className="relative m-2 w-full h-full">
        <div className="flex flex-col h-full w-full">
          <div className="flex flex-row">
            <p className="font-medium font-semibold">{title}</p>
            {type === ResourcesCostType.COST && (
              <ExplainerModal
                className={'p-px ml-2'}
                label={strings.common.detailViewCommon.estimatedCost}
                title={strings.common.detailViewCommon.estimatedCost}
                content={strings.common.detailViewCommon.estimatedCostExplanation}
              />
            )}
          </div>
          <div className="absolute top-0 right-0">{computeIcon()}</div>
          {!fetching ? (
            <div>
              <p className="font-bold text-2xl">{formatCurrentVal()}</p>
              <div className="flex flex-row items-center">{formatDifferenceVal()}</div>
            </div>
          ) : (
            <div className="flex flex-col gap-2 h-[70px] w-full">
              <SkeletonLoader className="w-20 h-full" />
              <SkeletonLoader className="w-32 h-full" />
              <SkeletonLoader className="w-full h-full" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
